import React from 'react';
import {
  Sofa,
  Armchair,
  Building2,
  Landmark,
  WashingMachine,
  Sparkle,
  DoorOpen,
  TreePine,
  Waves,
  Tag,
  Store,
  Truck,
  Users,
  Phone,
  LayoutDashboard,
} from 'lucide-react';

export interface CategoryItem {
  id: number;
  parentId?: number | null;
  name: string;
  icon?: React.ReactNode; // lucide-react icon — default render when no image/video is set
  image?: string;      // ← back to string; primary/first image
  images?: string[]; 
  video?: string;         // .mp4 / .webm — needs a <video> tag, so it's kept separate from image
  color?: string;         // card background color (hex) — used behind icon/image/video on category cards
  description?: string;   // short copy shown when the card is expanded — leave undefined until written
}




/* =========================================================
   1️⃣ HERO — top-level highlighted categories
   (used for hero section quick-filters / big tiles)
   Each entry is on ONE line — comment/uncomment with //
========================================================= */
const multi = (...paths: string[]) => ({ image: paths[0], images: paths });
export const Hero: CategoryItem[] = [
  { id: 10000001, name: 'hero image/video', icon: <Waves />, color: '#0F172A',  image: "/icons/ui/uihero.png" },

     { id: 10000002, name: 'app image/video', icon: <Phone />, color: '#ffcc00', ...multi(  "/icons/ui/ride.png","/icons/ui/food.png","/icons/ui/generalshop.png", "/icons/ui/grocery.png","/icons/ui/property.png",) },

];





/* =========================================================
   2️⃣ APP — "What's on Deck?" top-level categories
   Flat list, no subcategories.
   Each entry is on ONE line — comment/uncomment with //
========================================================= */

export const App: CategoryItem[] = [
  { id: 20000001, name: 'Vacation Rentals', description: "Browse stays for your next trip, from weekend getaways to extended stays.", icon: <Waves />, color: '#C7B7F5', image: "/icons/ui/uihome.png" }, // lavender
  //{ id: 20000002, name: 'Cleaners & Laundromats', description: "Book cleaners and laundry services near you, ready when you are.", icon: <WashingMachine />, color: '#FCE07A' }, // yellow
 // { id: 20000003, name: 'Offices', description: "Find private offices and coworking spaces available to lease.", icon: <DoorOpen />, color: '#8FC3F0' }, // blue
  //{ id: 20000004, name: 'Furniture', description: "Shop furniture for every room, from living room to outdoor.", icon: <Armchair />, color: '#A9E0C4' }, // mint green
  //{ id: 20000005, name: 'Vacant Rentals', description: "Explore empty units and land available to rent right now.", icon: <TreePine />, color: '#F4A9C8' }, // pink
  //{ id: 20000006, name: 'Property for Sale', description: "Browse houses, apartments, and land listed for sale.", icon: <Landmark />, color: '#F0A868' }, // orange
];

/* =========================================================
   3️⃣ NETWORK — "Join our growing network" cards
   (sellers, hosts, riders, etc.)
   Each entry is on ONE line — comment/uncomment with //
========================================================= */

export const Network: CategoryItem[] = [
 // { id: 30000001, name: 'Sellers', description: "Do you sell furniture or run a shop? Join our network to reach new customers and grow your business with ease.", icon: <Store />, color: '#FCE07A' }, // yellow
  { id: 30000002, name: 'Hosts', description: "List your property and start hosting guests or tenants. Get paid fast and manage bookings with ease.", icon: <Users />, color: '#A9E0C4' }, // green
 // { id: 30000003, name: 'Riders', description: "Join our league of delivery riders, earning on your schedule while helping the network run smoothly.", icon: <Truck />, color: '#8FC3F0' }, // blue
];




/* =========================================================
   4️⃣ FAQs — question / answer pairs for the FAQ section.
   `color` sets the background of the answer panel when that
   question is selected, `icon` renders inside that panel —
   same asterisk-in-a-circle treatment Chowdeck uses.
   Each entry is on ONE line — comment/uncomment with //
========================================================= */

export interface FAQItem {
  id: number;
  question: string;
  answer: string;
  icon?: React.ReactNode;
  color?: string;
}

export const FAQs: FAQItem[] = [
  { id: 1, question: 'What is Areluxe?', answer: "Areluxe is a global vacation rental and property platform connecting verified hosts with guests across 100+ countries. List your property, manage bookings, and get paid — all in one place.", icon: <Sparkle size={22} />, color: '#C7B7F5' }, // lavender
  { id: 2, question: 'How do I become a host?', answer: "Sign up, verify your identity, and list your property. Onboarding takes less than 10 minutes and you can start receiving bookings immediately.", icon: <Sparkle size={22} />, color: '#FCE07A' }, // yellow
  { id: 3, question: 'When do I get paid?', answer: "Payouts are processed within 24 hours of guest check-in and deposited directly to your bank account.", icon: <Sparkle size={22} />, color: '#8FC3F0' }, // blue
  { id: 4, question: 'Is it free to list?', answer: "Listing on Areluxe is completely free. We only charge a small service fee when a booking is confirmed — no upfront costs, no hidden fees.", icon: <Sparkle size={22} />, color: '#A9E0C4' }, // mint
  { id: 5, question: 'How do I become a seller partner?', answer: "Click 'Apply as a Partner' in the Sellers card. Our team will review and onboard you within 48 hours.", icon: <Sparkle size={22} />, color: '#F4A9C8' }, // pink
  { id: 6, question: 'Is my payment secure?', answer: "Yes. All transactions are processed through PCI-DSS compliant payment gateways with bank-grade encryption.", icon: <Sparkle size={22} />, color: '#F0A868' }, // orange
];

/* =========================================================
   4️⃣.b ONBOARDING TUTORIAL — step-by-step "become a host"
   walkthrough. `step` is the number shown on the card, `icon`
   renders inside the step badge, `color` sets that step's
   accent (badge background, progress dot, etc).
   Each entry is on ONE line — comment/uncomment with //
========================================================= */

export interface OnboardingStep {
  id: number;
  step: number;
  title: string;
  description: string;
  icon?: React.ReactNode;
  color?: string;
}

export const OnboardingTutorial: OnboardingStep[] = [
  { id: 1, step: 1, title: "Get the App", description: "Download Areluxe from the website, or grab the app on Google Play or the App Store.", icon: <Sparkle size={22} />, color: '#C7B7F5' }, // lavender
  { id: 2, step: 2, title: "Create Your Account", description: "Register a new account, or log in if you already have one — takes less than a minute.", icon: <Sparkle size={22} />, color: '#FCE07A' }, // yellow
  { id: 3, step: 3, title: "Find Become a Host", description: "Head to your Profile page and tap the 'Become a Host' or 'Start Earning' button.", icon: <Sparkle size={22} />, color: '#8FC3F0' }, // blue
  { id: 4, step: 4, title: "Name Your Property", description: "Enter your property name or business name — this is what guests will see first.", icon: <Sparkle size={22} />, color: '#A9E0C4' }, // mint
  { id: 5, step: 5, title: "Choose a Category", description: "Pick the category and subcategory that best fits your listing, from vacation rentals to offices.", icon: <Sparkle size={22} />, color: '#F4A9C8' }, // pink
  { id: 6, step: 6, title: "Go Live", description: "Confirm your details and publish — you're ready to start receiving bookings.", icon: <Sparkle size={22} />, color: '#F0A868' }, // orange
  { id: 7, step: 7, title: "List Your Property", description: "Navigate to your dashboard and list a property to start managing bookings and earnings.", icon: <LayoutDashboard size={22} />, color: '#C7B7F5' }, // lavender
];

/* =========================================================
   4️⃣.c PROPERTY LISTING FAQs — tabbed FAQ data used on the
   "list your property" (Tutorial) page only. Separate from
   `FAQs` above, which belongs to a different section/use.
   `tab` is the pill label shown in the tab bar; `items` is
   the list of q/a pairs shown in that tab's accordion.
   Each category is on ONE line — comment/uncomment with //
========================================================= */

export interface PropertyListingFAQItem {
  q: string;
  a: string;
}

export interface PropertyListingFAQCategory {
  id: number;
  tab: string;
  items: PropertyListingFAQItem[];
}

export const PropertyListingFAQs: PropertyListingFAQCategory[] = [
  { id: 1, tab: "Safety", items: [
    { q: "Who are ARELUXE guests?", a: "Our guests are travelers searching for quality, curated homes. They appreciate well-kept spaces, spend more per stay, and leave detailed reviews. We attract professionals, families, and long-stay travelers who treat your home with respect." },
    { q: "Can I choose who stays in my home?", a: "Yes. You control who stays in your property. ARELUXE has guest eligibility requirements and as a host you can set additional criteria — reviewing guest profiles and booking history before approving any reservation." },
    { q: "How can I protect my property from potential damage?", a: "ARELUXE includes built-in host compensation protection. You can require a security deposit, and our automated system handles damage claims, refunds, and guest accountability. You're never left dealing with disputes alone." },
    { q: "What happens if a guest cancels last-minute?", a: "Our 3-tier cancellation policy system ensures you're compensated based on your chosen policy. Automatic payouts are triggered immediately — no manual claims, no waiting." },
  ]},
  { id: 2, tab: "Management", items: [
    { q: "How does ARELUXE handle payments?", a: "All payments are processed securely via Stripe. VAT, transaction fees, and payouts are handled automatically. Your earnings dashboard shows real-time income and forecasts future payouts." },
    { q: "Do I need to manage guest communication?", a: "ARELUXE automates guest messaging from booking confirmation through check-out. You can personalise templates but the platform handles routine communication so you can focus on hosting." },
    { q: "Can I list multiple properties?", a: "Absolutely. ARELUXE is built for multi-property hosts. Each listing has its own calendar, pricing rules, and analytics — all accessible from one unified dashboard." },
  ]},
  { id: 3, tab: "Pricing", items: [
    { q: "What commission does ARELUXE charge?", a: "% varies on booking value use estimator. ARELUXE charges zero commission on bookings. There are no listing fees or hidden charges. We earn through optional add-ons and marketing packages — your earnings stay yours." },
    { q: "How does smart pricing work?", a: "Our AI-driven pricing engine analyses local demand, seasonality, competitor rates, and booking patterns to suggest optimal nightly prices. You always have final control — accept suggestions or set your own rates." },
    { q: "When do I receive my payouts?", a: "Payouts are processed automatically after guest check-in. Standard transfer time is 1–3 business days depending on your bank. You can view pending, processing, and completed payouts in real-time." },
  ]},
  { id: 4, tab: "Why ARELUXE", items: [
    { q: "0% Commission", a: "Keep every euro you earn. No listing fees, no booking cuts — we earn only when you use optional add-ons." },
    { q: "Host Protection", a: "Compensation protection, automated damage claims, and secure payments so you're never exposed." },
    { q: "Smart Pricing AI", a: "Dynamic pricing engine that adjusts nightly rates based on demand, season and local competition." },
    { q: "Curated Guests", a: "ARELUXE attracts verified, reliable travelers. You control who books your home." },
    { q: "Full Management", a: "From listing setup to guest communication and checkout — everything is automated and handled." },
    { q: "Extra Visibility", a: "Your listing is featured across ARELUXE's marketing channels and partner platforms." },
  ]},
];

/* =========================================================
   5️⃣ TERRITORIES — map card locations & featured stays
   City pins for the interactive map, and featured stay listings
   Each entry is on ONE line — comment/uncomment with //
========================================================= */

export interface CityPin {
  name: string;
  top: string;
  left: string;
  color: string;
}

export const CITY_PINS: CityPin[] = [
//  { name: "Lekki", top: "62%", left: "58%", color: "#F4A9C8" },
 // { name: "Ikoyi", top: "40%", left: "48%", color: "#8FC3F0" },
 // { name: "Abuja", top: "18%", left: "20%", color: "#C7B7F5" },
 // { name: "Accra", top: "30%", left: "72%", color: "#FCE07A" },
  { name: "Nairobi", top: "70%", left: "22%", color: "#A9E0C4" },
 // { name: "Cape Town", top: "78%", left: "68%", color: "#F0A868" },
];

export const FEATURED_STAYS: string[] = [
  "Ocean Breeze Villa — Lekki",
  "Skyline Loft — Ikoyi",
  "Palm Grove Cottage — Accra",
  "Savanna House — Nairobi",
  "Table Bay Studio — Cape Town",
  "Riverside Retreat — Abuja",
];

export const Territories = {
  CITY_PINS,
  FEATURED_STAYS,
};

export const Delivery: CategoryItem[] = [

   { id: 7000802, name: 'Fast Delivery', description: "Fast delivery to your location ", icon: <Waves />, color: '#da0000', image: "/icons/ui/marketing/courier.png" },
];

export const GroceryMarketing: CategoryItem[] = [
  { id: 8000802, name: 'Fresh Groceries', description: "Fresh Groceries Delivered to u ", icon: <Waves />, color: '#da0000', image: "/icons/ui/marketing/grocery.png" },
   { id: 8000802, name: 'Fresh Groceries', description: "Fresh Groceries Delivered to u ", icon: <Waves />, color: '#da0000', image: "/icons/ui/marketing/courier.png" },
];




export const FoodMarketing: CategoryItem[] = [
  { id: 9000802, name: 'Tasty Food', description: "Hot Delicacies Delivered to You ", icon: <Waves />, color: '#ff8c00', image: "/icons/ui/marketing/food.png" },

];


/* =========================================================
   6️⃣ UICategories — single grouped export
========================================================= */

export const UICategories = {
  Hero,
  App,
  Network,
  FAQs,
  OnboardingTutorial,
  PropertyListingFAQs,
  Territories,
};

export default UICategories;